/* HistoryViewComponent.js */

"use strict";
const _ = require("lodash");
const SnapshotColumn = require("./column_renderers/snapshot/SnapshotComponent");
const LinksColumn = require("./column_renderers/links/LinksComponent");

const BaseActivityViewComponent = require("../../../../lib/activity/BaseActivityViewComponent");
const AuditStatusColumn = require("./column_renderers/audit_status/AuditStatusComponent");

const Renderer = require("./HistoryViewRenderer");
const uString = require("underscore.string");

const PagingActivityFeed = require("./PagingHistoryFeed");

_.mixin(uString.exports());

class HistoryViewComponent extends BaseActivityViewComponent {
    constructor(
        logger,
        ConfigAuditExecutionDef,
        gameEditor,
        viewState,
        io,
        utils,
        userSession,
        solutionsManager,
        storageFactory,
        MUF,
        DeviceEntityDef,
        CustomFieldsEntityDef,
        SiteEntityDef,
        CommandSetEntityDef,
        TagsEntityDef
    ) {
        super(logger, gameEditor, io, utils, userSession, solutionsManager, storageFactory);

        this._viewState = viewState || {};
        this._configAuditExecutionEntityDef = ConfigAuditExecutionDef;
        this._deviceEntityDef = DeviceEntityDef;
        this._customFieldsEntityDef = CustomFieldsEntityDef;
        this._siteEntityDef = SiteEntityDef;
        this._commandSetEntityDef = CommandSetEntityDef;
        this._tagsEntityDef = TagsEntityDef;
        this._columnConfig = {
            id: {
                label: "Execution Id",
                sortable: false,
                resizable: false,
                priority: 1,
                shown: false,
            },
            title: {
                label: "Name",
                width: 250,
                path: "title",
                priority: 2,
            },
            completedOn: {
                label: "End Time",
                columnRendererComponent: SnapshotColumn,
                width: 150,
                priority: 3,
            },
            executedBy: {
                label: "Executed By",
                priority: 4,
            },
            status: {
                label: "Status",
                priority: 5,
                columnRendererComponent: AuditStatusColumn,
            },
            deviceCount: {
                label: "Total Devices",
                width: 150,
                priority: 6,
            },
            deviceSuccessCount: {
                label: "Audited Devices",
                width: 120,
                priority: 7,
            },
            devicesSkippedCount: {
                label: "Skipped Devices",
                width: 120,
                priority: 8,
            },
            devicesInViolationCount: {
                label: "Device Violations",
                width: 150,
                priority: 9,
            },
            deviceErrorCount: {
                label: "Device Errors",
                width: 150,
                priority: 10,
            },
            violationCount: {
                label: "Total Violations",
                width: 150,
                priority: 11,
            },
            ruleCount: {
                label: "Total Rules",
                width: 100,
                priority: 12,
            },
            links: {
                label: "Actions",
                columnRendererComponent: LinksColumn,
                context: {
                    gameEditor: this._gameEditor,
                    triggerType: this._entity,
                    logger: this._logger,
                    modelUserFeedback: MUF,
                    parentContext: this,
                },
                entityColumn: true,
                sortable: false,
                priority: 13,
            },
        };

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _patchSorting(sorting) {
        let patchedSorting = _.clone(sorting);

        return patchedSorting;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/
    get viewState() {
        return this.getViewState();
    }
    getViewState() {
        return this._viewState;
    }

    get columnConfig() {
        return this._columnConfig;
    }

    shutdown() {
        let promises = [];
        if (this._activityFeed && this._activityFeed.shutdown) {
            promises.push(this._entity.stopRelatedExecutionsFeed());
        } else {
            promises.push(Promise.resolve());
        }

        return Promise.all(promises)
            .then(() => {
                return super.shutdown();
            })
            .then(
                () => {
                    this._renderer = undefined;
                    this._columnConfig = undefined;
                    this._viewState = undefined;
                    this._activityFeed = undefined;
                },
                (err) => {
                    this._logger.warn({error: err}, "HistoryViewComponent.shutdown() error.");
                }
            );
    }

    whenModelIsReady() {
        const additionalDataFields = [
            "executedOn",
            "status",
            "errorMsg",
            "auditPolicy",
            "devicesNotInViolationCount",
            "majorViolationCount",
            "minorViolationCount",
            "criticalViolationCount",
            "infoViolationCount",
            "deviceTarget",
        ];
        return this._gameEditor.whenPolicyIsReady().then(() => {
            const userSorting = this._userGridData.getSorting() || {completedOn: -1};
            const patchedSort = this._patchSorting(userSorting);
            this._entity = this._gameEditor.getAuditPolicy();
            this._activityFeed = new PagingActivityFeed({
                feedKey: this._gridKey + "_feed",
                userSession: this._userSession,
                entityDef: this._entity,
                columnConfig: this._columnConfig,
                additionalDataFields,
                sort: patchedSort,
                relatedEntityDef: this._configAuditExecutionEntityDef,
            });
            this._registerListeners();
            return this.whenFeedIsReady();
        });
    }

    whenFeedIsReady() {
        return this._activityFeed.whenFeedReady().catch((err) => {
            this._logger.warn({error: err}, "PagingHistoryFeed.whenFeedReady() error.");
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = HistoryViewComponent;

HistoryViewComponent.$inject = [
    "$teve.logger",
    "$teve.entities.ConfigAuditExecution",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.io",
    "$teve.utils",
    "$teve.userSession",
    "$teve.solutionsManager",
    "$teve.storageFactory",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
    "$teve.entities.CustomFields",
    "$teve.entities.Site",
    "$teve.entities.CommandSet",
    "$teve.entities.Tags",
];

HistoryViewComponent.$displayDetails = {
    type: "View",
    name: "Audit Execution Summary",
    icon: "configdrift.audit_policy_history",
    shown: true,
    title: "Audit Execution Summary",
    order: 4,
};
